




import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Chart } from 'highcharts-vue';
import More from 'highcharts/highcharts-more';
import Highcharts from 'highcharts';
import vue from 'vue';
import { colors } from '@/config';
import { highchartsSpacing } from '@/utils/chartPresentationSizing';

More(Highcharts);

@Component({ components: { Chart } })
export default class DynamicColumnChart extends Vue {
  @Prop() profitloss: any;
  @Prop() balance: any;
  @Prop() cashflow: any;
  @Prop() financialRatio: any;

  @Prop() dark!: boolean;
  @Prop() containerWidth: any;
  @Prop() currentLanguage: any;

  chartOptions: any = {};

  update() {
    this.$nextTick(() => {
      const chart = this.$refs.chart as any;
      if (chart && chart.chart) {
        chart.chart.reflow();
      }
    });

    this.chartOptions = {
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        ...highchartsSpacing(this.containerWidth),
      },

      credits: {
        enabled: false,
      },

      title: {
        text: '',
      },

      xAxis: {
        type: 'category',
        labels: {
          autoRotation: [0],
          staggerLines: 1,
          padding: this.transformPx(10),
          style: {
            fontSize: this.dark ? '1cqw' : '1cqw',
            textOverflow: 'none',
            whiteSpace: 'normal',
            color: this.dark ? colors.Light : colors.Black,
          },
        },
        lineColor: this.dark ? colors.Black : colors.LightGrey,
      },

      yAxis: {
        title: {
          text: '',
        },
        labels: {
          style: {
            fontSize: this.dark ? '1cqw' : '1cqw',
            color: this.dark ? colors.Light : colors.Black,
            fontWeight: this.dark ? 400 : 600,
          },
          formatter() {
            return (vue as any).options.filters.KR((this as any).value);
          },
        },
        tickPixelInterval: this.dark ? this.transformPx(200) : 175,
        gridLineColor: this.dark ? colors.Black : colors.LightGrey,
        plotLines: [
          {
            color: colors.Black,
            width: this.dark ? this.transformPx(3) : 2,
            value: 0,
            zIndex: 20,
          },
        ],
        softMin: 0,
        softMax: 0,
      },

      legend: {
        verticalAlign: 'top',
        align: 'right',
        enabled: true,
        itemStyle: {
          fontSize: this.dark ? '1.5cqw' : '1cqw',
          fontWeight: 'normal',
          color: this.dark ? colors.Light : colors.Black,
        },
        itemHoverStyle: {
          color: this.dark ? colors.Light : colors.Black,
        },
        symbolWidth: this.transformPx(50),
      },

      tooltip: {
        formatter() {
          return (vue as any).options.filters.TKR((this as any).y);
        },
        style: {
          fontSize: '1.5cqw',
          color: this.dark ? colors.Light : colors.Black,
        },
        backgroundColor: this.dark ? colors.Dark : colors.Light,
      },

      series: this.getData(),
    };
  }

  mounted() {
    this.update();
  }

  @Watch('profitloss', { immediate: false, deep: true })
  profitlossChanged() {
    this.update();
  }

  @Watch('balance', { immediate: false, deep: true })
  balanceChanged() {
    this.update();
  }

  @Watch('cashflow', { immediate: false, deep: true })
  cashflowChanged() {
    this.update();
  }

  @Watch('containerWidth', { immediate: false, deep: true })
  containerWidthChanged() {
    this.update();
  }

  getData(): any {
    switch (this.financialRatio) {
      case 'turnover':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: this.dark ? colors.Black : colors.Black,
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: p.A,
                // tslint:disable:max-line-length
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'operatingResult':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: p.K,
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'yearResult':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: p.R,
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'totalAssets':
        if (!this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.balance.sheets
              .map((b: any, i: number) => ({
                name:
                  b.DATE_TO.slice(5, 7) === '12'
                    ? b.DATE_TO.slice(0, 4)
                    : b.DATE_TO.slice(0, 7),
                y: b.L,
                // tslint:disable:max-line-length
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'totalDebt':
        if (!this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.balance.sheets
              .map((b: any, i: number) => ({
                name:
                  b.DATE_TO.slice(5, 7) === '12'
                    ? b.DATE_TO.slice(0, 4)
                    : b.DATE_TO.slice(0, 7),
                y: b.R,
                // tslint:disable:max-line-length
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'equity':
        if (!this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.balance.sheets
              .map((b: any, i: number) => ({
                name:
                  b.DATE_TO.slice(5, 7) === '12'
                    ? b.DATE_TO.slice(0, 4)
                    : b.DATE_TO.slice(0, 7),
                y: b.M,
                // tslint:disable:max-line-length
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'adjustedEquity':
        if (!this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.balance.sheets
              .map((b: any, i: number) => ({
                name:
                  b.DATE_TO.slice(5, 7) === '12'
                    ? b.DATE_TO.slice(0, 4)
                    : b.DATE_TO.slice(0, 7),
                y: b.M + 0.794 * b.N,
                // tslint:disable:max-line-length
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'operatingCashflow':
        if (!this.cashflow) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y: c.OperatingCashflow,
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'cash':
        if (!this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.balance.sheets
              .map((b: any, i: number) => ({
                name:
                  b.DATE_TO.slice(5, 7) === '12'
                    ? b.DATE_TO.slice(0, 4)
                    : b.DATE_TO.slice(0, 7),
                y: b.J,
                // tslint:disable:max-line-length
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'workingCapital':
        if (!this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.balance.sheets
              .map((b: any, i: number) => ({
                name:
                  b.DATE_TO.slice(5, 7) === '12'
                    ? b.DATE_TO.slice(0, 4)
                    : b.DATE_TO.slice(0, 7),
                y: b.K - b.J - b.Q + b.U,
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'workingCapitalDelta':
        if (!this.cashflow) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: colors.Positive,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y: -c.WorkingCaptialCashflow,
                color:
                  c.WorkingCaptialCashflow > 0
                    ? colors.Positive
                    : colors.Negative,
              }))
              .reverse(),
          },
        ];
      case 'investmentsCashflow':
        if (!this.cashflow) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: colors.Negative,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y: c.InvestmentsCashflow,
                color:
                  c.InvestmentsCashflow > 0 ? colors.Positive : colors.Negative,
              }))
              .reverse(),
          },
        ];
      case 'totalCashflow':
        if (!this.cashflow) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: colors.Positive,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y: c.CashFlow,
                color: c.CashFlow > 0 ? colors.Positive : colors.Negative,
              }))
              .reverse(),
          },
        ];
      case 'freeCashflow':
        if (!this.cashflow) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: colors.Positive,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y: c.CashFlow - c.FinancingCashflow,
                color:
                  c.CashFlow - c.FinancingCashflow > 0
                    ? colors.Positive
                    : colors.Negative,
              }))
              .reverse(),
          },
        ];
      case 'interestBearingLiabilities':
        if (!this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.balance.sheets
              .map((b: any, i: number) => ({
                name:
                  b.DATE_TO.slice(5, 7) === '12'
                    ? b.DATE_TO.slice(0, 4)
                    : b.DATE_TO.slice(0, 7),
                y: b.U + b.P,
                // tslint:disable:max-line-length
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'interestBearingLiabilitiesDelta':
        if (!this.cashflow) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: colors.Negative,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y: c.InterestLiabilitiesDelta,
                color:
                  c.InterestLiabilitiesDelta > 0
                    ? colors.Positive
                    : colors.Negative,
              }))
              .reverse(),
          },
        ];
      case 'ownersCashflow':
        if (!this.cashflow) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: colors.Negative,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y:
                  c.EquityWithoutNetProfitDelta +
                  c.SubscribedCapitalUnpaidDelta,
                // tslint:disable:max-line-length
                color:
                  c.EquityWithoutNetProfitDelta +
                    c.SubscribedCapitalUnpaidDelta >
                  0
                    ? colors.Positive
                    : colors.Negative,
              }))
              .reverse(),
          },
        ];
      case 'financingCashflow':
        if (!this.cashflow) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: colors.Negative,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y: c.FinancingCashflow,
                color:
                  c.FinancingCashflow > 0 ? colors.Positive : colors.Negative,
              }))
              .reverse(),
          },
        ];
      case 'operatingCashflowPosNeg':
        if (!this.cashflow) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: colors.Positive,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y: c.OperatingCashflow,
                color:
                  c.OperatingCashflow > 0 ? colors.Positive : colors.Negative,
              }))
              .reverse(),
          },
        ];
      case 'grossProfit':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: p.A + p.D,
                // tslint:disable:max-line-length
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'personellCosts':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: -p.G,
                // tslint:disable:max-line-length
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'grossProfitExPersonellCosts':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: p.A + p.D + p.G,
                // tslint:disable:max-line-length
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'stock':
        if (!this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.balance.sheets
              .map((b: any, i: number) => ({
                name:
                  b.DATE_TO.slice(5, 7) === '12'
                    ? b.DATE_TO.slice(0, 4)
                    : b.DATE_TO.slice(0, 7),
                y: b.F,
                // tslint:disable:max-line-length
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
      case 'otherExternalCosts':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Secondary : colors.Primary,
            pointPadding: 0,
            borderColor: colors.Black,
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: -p.F,
                // tslint:disable:max-line-length
                color: this.dark
                  ? i === 0
                    ? colors.Tertiary
                    : colors.Secondary
                  : i === 0
                  ? colors.Secondary
                  : colors.Primary,
              }))
              .reverse(),
          },
        ];
    }
  }

  getName() {
    switch (this.financialRatio) {
      case 'turnover':
        return this.currentLanguage === 'en'
          ? 'Net sales'
          : Vue.prototype.$t('Nettoomsättning');
      case 'operatingResult':
        return this.currentLanguage === 'en'
          ? 'Operating profit/loss'
          : Vue.prototype.$t('Rörelseresultat');
      case 'yearResult':
        return this.currentLanguage === 'en'
          ? 'Net profit/loss'
          : Vue.prototype.$t('Årets resultat');
      case 'totalAssets':
        return this.currentLanguage === 'en'
          ? 'Total assets'
          : Vue.prototype.$t('Balansomslutning');
      case 'totalDebt':
        return this.currentLanguage === 'en'
          ? 'Total debts'
          : Vue.prototype.$t('Totala skulder');
      case 'equity':
        return this.currentLanguage === 'en'
          ? 'Equity'
          : Vue.prototype.$t('Eget kapital');
      case 'adjustedEquity':
        return this.currentLanguage === 'en'
          ? 'Adjusted equity'
          : Vue.prototype.$t('Justerat eget kapital');
      case 'operatingCashflow':
        return this.currentLanguage === 'en'
          ? 'Operating cashflows'
          : Vue.prototype.$t('Operativa kassaflöden');
      case 'cash':
        return this.currentLanguage === 'en'
          ? 'Cash'
          : Vue.prototype.$t('Kassa & bank');
      case 'workingCapital':
        return this.currentLanguage === 'en'
          ? 'Working capital (excl. cash)'
          : Vue.prototype.$t('Bindning i rörelsekapital (exkl. kassa & bank)');
      case 'workingCapitalDelta':
        return this.currentLanguage === 'en'
          ? 'Change Working capital (excl. cash)'
          : Vue.prototype.$t(
              'Förändring av bindning i rörelsekapital (exkl. kassa & bank)',
            );
      case 'investmentsCashflow':
        return this.currentLanguage === 'en'
          ? 'Cashflow from investments'
          : Vue.prototype.$t('Kassaflöde investeringar');
      case 'totalCashflow':
        return this.currentLanguage === 'en'
          ? 'Total cashflow'
          : Vue.prototype.$t('Totalt kassaflöde');
      case 'freeCashflow':
        return this.currentLanguage === 'en'
          ? 'Free cashflow'
          : Vue.prototype.$t('Fritt kassaflöde');
      case 'interestBearingLiabilities':
        return this.currentLanguage === 'en'
          ? 'Interest-bearing liabilities'
          : Vue.prototype.$t('Räntebärande skulder');
      case 'interestBearingLiabilitiesDelta':
        return this.currentLanguage === 'en'
          ? 'Change Interest-bearing liabilities'
          : Vue.prototype.$t('Förändring av räntebärande skulder');
      case 'ownersCashflow':
        return this.currentLanguage === 'en'
          ? 'Owners capital'
          : Vue.prototype.$t('Ägarkapital');
      case 'financingCashflow':
        return this.currentLanguage === 'en'
          ? 'Cashflow from financing'
          : Vue.prototype.$t('Kassaflöde avseende finansiering');
      case 'operatingCashflowPosNeg':
        return this.currentLanguage === 'en'
          ? 'Operating cashflow'
          : Vue.prototype.$t('Operativa kassaflöden');
      case 'grossProfit':
        return this.currentLanguage === 'en'
          ? 'Gross profit/loss'
          : Vue.prototype.$t('Bruttoresultat');
      case 'personellCosts':
        return this.currentLanguage === 'en'
          ? 'Cost for personell'
          : Vue.prototype.$t('Personalkostnader');
      case 'grossProfitExPersonellCosts':
        return this.currentLanguage === 'en'
          ? 'Gross profit after cost for personell'
          : Vue.prototype.$t('Bruttoresultat efter personalkostnader');
      case 'stock':
        return this.currentLanguage === 'en'
          ? 'Stock'
          : Vue.prototype.$t('Lager');
      case 'otherExternalCosts':
        return this.currentLanguage === 'en'
          ? 'Other external costs'
          : Vue.prototype.$t('Övriga externa kostnader');
    }
  }

  transformPx(value: any) {
    if (value * (this.containerWidth / 1920) < 1) {
      return 1;
    }
    return value * (this.containerWidth / 1920);
  }
}
